import { filterDuplicates } from '../DesignTool/utils';

const emptyPanelForSegment = (segment = {}) => ({
  id: segment?.id,
  azimuth: segment?.azimuth ?? 0,
  horizontal: {
    height: 0,
    width: 0,
    points: [],
    selected: [],
  },
  vertical: {
    height: 0,
    width: 0,
    points: [],
    selected: [],
  },
});

export const alignPanelsToSegments = (panels = [], segments = []) => {
  if (!Array.isArray(panels)) return [];
  if (!Array.isArray(segments) || segments.length === 0) return panels;

  const panelsById = new Map();
  panels.forEach((panel) => {
    if (panel?.id !== undefined && panel?.id !== null) {
      panelsById.set(String(panel.id), panel);
    }
  });

  const canAlignById =
    panelsById.size > 0 &&
    segments.some((segment) => segment?.id !== undefined && segment?.id !== null);
  const alreadyAligned =
    panels.length === segments.length &&
    segments.every((segment, index) => {
      const panel = panels[index];
      if (
        segment?.id === undefined ||
        segment?.id === null ||
        panel?.id === undefined ||
        panel?.id === null
      ) {
        return true;
      }
      return String(segment.id) === String(panel.id);
    });

  if (!canAlignById || alreadyAligned) return panels;

  return segments.map((segment, index) => {
    const panelById =
      segment?.id !== undefined && segment?.id !== null
        ? panelsById.get(String(segment.id))
        : null;
    if (panelById) return panelById;

    const panelByIndex = panels[index];
    if (panelByIndex && (panelByIndex.id === undefined || panelByIndex.id === null)) {
      return { ...panelByIndex, id: segment?.id ?? panelByIndex.id };
    }

    return emptyPanelForSegment(segment);
  });
};

export default (API, uid) => {
  const canvasId = uid;
  const dropKeys = (obj, keys) => {
    const next = { ...(obj || {}) };
    keys.forEach((k) => {
      if (k in next) delete next[k];
    });
    return next;
  };

  const sanitizeSegmentsForLegacy = (segments = []) =>
    (segments || []).map((seg) => {
      if (!seg || typeof seg !== 'object') return seg;
      const {
        annual_kwh,
        annualKwh,
        clipping_kwh,
        clippingKwh,
        monthly_clipping_kwh,
        monthlyClippingKwh,
        selected_panel_count,
        selectedPanelCount,
        stringing_data,
        stringingData,
        inverters_used,
        invertersUsed,
        dc_ac_ratio,
        dcAcRatio,
        ...rest
      } = seg;
      return rest;
    });

  const makeCanvasSaveError = (message, err, details = {}) => {
    const saveError = new Error(message);
    saveError.cause = err;
    saveError.response = err?.response;
    saveError.canvasSave = details;
    return saveError;
  };

  const logCanvasSaveError = (message, err, details = {}) => {
    console.warn('[CanvasAPI.saveCanvas]', message, {
      canvasId,
      status: err?.response?.status,
      response: err?.response?.data ?? err?.message,
      ...details,
    });
  };

  const parseJsonIfString = (value) => {
    if (value == null) return value;
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      return value;
    }
  };

  const toNumOr = (value, fallback) => {
    if (value === undefined || value === null) return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };
  const normalizeNumericList = (value, length, fallback) => {
    const source = Array.isArray(value) ? value : [];
    return Array.from({ length }, (_, index) =>
      toNumOr(source[index], fallback)
    );
  };
  const normalizeFixedList = (value, length, fallback) =>
    normalizeNumericList(value, length, fallback).map((val) =>
      val.toFixed(4)
    );
  const normalizeLines = (segment) => {
    const geometryLength = Array.isArray(segment?.geometry)
      ? segment.geometry.length
      : 0;
    const lines = Array.isArray(segment?.lines) ? segment.lines : [];
    const expectedLength = geometryLength || lines.length;
    return Array.from(
      { length: expectedLength },
      (_, index) => lines[index] || ''
    );
  };

  // helper to load data from API
  const loadData = (data) => {
    // console.log(data, 'Load');
    const shade = data.shaded;
    const colorRange = (data.color_range ? data.color_range : '0,2450')
      .split(',')
      .map(Number);
    const rawPanels = data.panels || [];
    const annotations = data.annotations || [];
    const selectedPanel = data.selected_panel || null;
    const selectedPanelId =
      data.selected_panel_id ??
      data.selectedPanelId ??
      (data.selected_panel && data.selected_panel.id) ??
      null;
    const selectedInverter = data.selected_inverter || null;
    const selectedInverterId =
      data.selected_inverter_id ??
      data.selectedInverterId ??
      (data.selected_inverter && data.selected_inverter.id) ??
      null;
    const selectedPanelSpec = data.selected_panel_spec || null;
    const selectedInverterSpec = data.selected_inverter_spec || null;
    const inverterQuantity = data.inverter_quantity;
    const primaryInverterAutoQuantity = data.primary_inverter_auto_quantity;
    const additionalInverters = data.additional_inverters || [];
    const losses = data.losses ?? null;
    const useTargetProduction = Boolean(data.use_target_production);
    const targetProductionKwh = data.target_production_kwh ?? 0;
    const maxDcAcRatio = data.max_dc_ac_ratio ?? null;
    const onlySelectedPanels = Boolean(data.only_selected_panels);
    const weatherDataset = data.weather_dataset || null;
    const pitchInDegrees =
      data.pitch_in_degrees !== undefined && data.pitch_in_degrees !== null
        ? Boolean(data.pitch_in_degrees)
        : true;

    const totalAnnualKwh = data.total_annual_kwh || 0;
    const totalClippingKwh = data.total_clipping_kwh || 0;
    const dcAcRatio = data.dc_ac_ratio ?? null;
    const stringingData = data.stringing_data || null;
    const invertersUsed = data.inverters_used || null;
    const systemSizeKw = data.system_size_kw ?? null;
    const mpptCountTotal = data.mppt_count_total ?? null;

    const parseMonthly = (val) => {
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') {
        return val
          .split(',')
          .map((x) => (x === '' || x === null || x === undefined ? 0 : Number(x)));
      }
      return Array(12).fill(0);
    };
    const totalMonthlyKwh = parseMonthly(data.total_monthly_kwh);
    const totalMonthlyClippingKwh = parseMonthly(data.total_monthly_clipping_kwh);

    // get segments data
    const segments = [];
    (data.segments || [])
      .map(({
        lines,
        height,
        azimuth,
        panel_orientation: panelOrientation,
        panel_axis: panelAxis,
        ac_monthly: acMonthly,
        poa_monthly: poaMonthly,
        solar_access: solarAccess,
        ...segment
      }) => {
        const lineArray =
          Array.isArray(lines) && lines.length > 0
            ? lines
            : Array(segment.geometry.length).fill('');
        const acAnnual = acMonthly.reduce((a, b) => a + b, 0);
        const duplicates = filterDuplicates(segment.geometry);
        if (duplicates[0].length > 2) {
          segments.push({
            ...segment,
            shape: 'Polygon',
            height: +height,
            azimuth: +azimuth,
            lines: lineArray.map((lineLabel) => (lineLabel === 'None' ? '' : lineLabel)),
            panelOrientation,
            panelAxis,
            acMonthly,
            poaMonthly,
            acAnnual,
            solarAccess,
          });
        }
      });

    // get obstacle data
    const obstacles = data.obstacles.map(({ radius, height, shape, ...obstacle }) => ({
      ...obstacle,
      height: +height,
      radius: shape === 'Polygon' ? null : +radius,
      shape,
    }));

    // get tree data
    const trees = data.trees.map(({ radius, height, trunk_height, ...tree }) => ({
      ...tree,
      height: +height,
      trunkHeight: trunk_height !== undefined && trunk_height !== null
        ? +trunk_height
        : undefined,
    }));

    // add empty panel containers
    /* const panels = segments.map(({id, azimuth}) => {
          return {
            id,
            azimuth,
            horizontal: {height: 0, width: 0, points: []},
            vertical: {height: 0, width: 0, points: []},
            ac_monthly: [],
            solar_access: 1,
          };
        }) */

    // return all data
    return {
      segments,
      obstacles,
      trees,
      shade,
      colorRange,
      panels: alignPanelsToSegments(rawPanels, segments),
      annotations,
      weather_dataset: weatherDataset,
      selected_panel: selectedPanel,
      selected_panel_id: selectedPanelId,
      selected_inverter: selectedInverter,
      selected_inverter_id: selectedInverterId,
      selected_panel_spec: selectedPanelSpec,
      selected_inverter_spec: selectedInverterSpec,
      inverter_quantity: inverterQuantity,
      primary_inverter_auto_quantity: primaryInverterAutoQuantity,
      additional_inverters: additionalInverters,
      losses,
      total_annual_kwh: totalAnnualKwh,
      total_monthly_kwh: totalMonthlyKwh,
      total_clipping_kwh: totalClippingKwh,
      total_monthly_clipping_kwh: totalMonthlyClippingKwh,
      dc_ac_ratio: dcAcRatio,
      stringing_data: stringingData,
      inverters_used: invertersUsed,
      system_size_kw: systemSizeKw,
      mppt_count_total: mpptCountTotal,
      use_target_production: useTargetProduction,
      target_production_kwh: targetProductionKwh,
      max_dc_ac_ratio: maxDcAcRatio,
      only_selected_panels: onlySelectedPanels,
      pitch_in_degrees: pitchInDegrees,
    };
  };

  // recover data from previously saved canvas
  const getCanvas = () => (
    API.retrieve(uid).then((data) => loadData(data))
  );

  // save all data
  const saveCanvas = (canvasData, options = {}) => {
    const strict = options?.strict === true;
    const {
      segments, obstacles, trees, colorRange, ...canvas
    } = canvasData;
    if (segments) {
      canvas.segments = segments.map(({
        acMonthly,
        poaMonthly,
        solarAccess,
        panelOrientation,
        panelAxis,
        tsrf,
        tof,
        ...segment
      }) => ({
        ...segment,
        lines: normalizeLines(segment),
        panel_orientation: panelOrientation,
        panel_axis: panelAxis ?? segment.panel_axis ?? null,
        ac_monthly: normalizeNumericList(acMonthly, 12, 0),
        poa_monthly: normalizeNumericList(poaMonthly, 12, 0),
        solar_access: normalizeFixedList(solarAccess, 13, 1),
        tsrf: normalizeFixedList(tsrf, 13, 1),
        tof: normalizeFixedList(tof, 13, 1),
      }));
    }
    if (obstacles) {
      canvas.obstacles = obstacles;
    }
    if (trees) {
      canvas.trees = trees.map(({ trunkHeight, ...tree }) => ({
        ...tree,
        trunk_height: trunkHeight !== undefined && trunkHeight !== null
          ? +trunkHeight
          : null,
      }));
    }
    if (colorRange) canvas.color_range = colorRange.join(',');
    const legacyDrop = [
      'selected_panel_id',
      'selected_inverter_id',
      'selected_panel',
      'selected_inverter',
      'selected_panel_spec',
      'selected_inverter_spec',
      'inverter_quantity',
      'primary_inverter_auto_quantity',
      'additional_inverters',
      'losses',
      'total_annual_kwh',
      'total_monthly_kwh',
      'total_clipping_kwh',
      'total_monthly_clipping_kwh',
      'dc_ac_ratio',
      'system_size_kw',
      'mppt_count_total',
      'inverter_count_total',
      'stringing_data',
      'stringing_panel_index_mode',
      'inverters_used',
      'production_per_panel',
      'production_per_string',
      'hourly_production',
      'use_target_production',
      'target_production_kwh',
      'max_dc_ac_ratio',
      'only_selected_panels',
      'pitch_in_degrees',
      'weather_dataset'
    ];

    return API.update(uid, canvas, true)
      .then((data) => loadData(data))
      .catch((err) => {
        const status = err?.response?.status;
        logCanvasSaveError('primary update failed', err, {
          fields: Object.keys(canvas),
        });
        if (status !== 400 && status !== 404) {
          if (strict) {
            return Promise.reject(
              makeCanvasSaveError('Canvas save failed', err, {
                canvasId,
                fields: Object.keys(canvas),
              })
            );
          }
          return Promise.resolve(canvasData);
        }

        const droppedKeys = legacyDrop.filter((key) => key in canvas);
        const legacyCanvas = dropKeys(canvas, legacyDrop);
        if (legacyCanvas.segments) {
          legacyCanvas.segments = sanitizeSegmentsForLegacy(legacyCanvas.segments);
        }

        return API.update(canvasId, legacyCanvas, true)
          .then(
            (data) => {
              if (strict && droppedKeys.length > 0) {
                return Promise.reject(
                  makeCanvasSaveError(
                    'Canvas save used legacy fallback and omitted fields',
                    err,
                    {
                      canvasId,
                      droppedKeys,
                    }
                  )
                );
              }
              return loadData(data);
            },
            (fallbackErr) => {
              logCanvasSaveError('legacy fallback failed', fallbackErr, {
                fields: Object.keys(legacyCanvas),
                droppedKeys,
              });
              if (strict) {
                return Promise.reject(
                  makeCanvasSaveError('Canvas save fallback failed', fallbackErr, {
                    canvasId,
                    fields: Object.keys(legacyCanvas),
                    droppedKeys,
                  })
                );
              }
              return Promise.resolve(canvasData);
            }
          );
      });
  };

  return {
    saveCanvas,
    getCanvas,
  };
};
